import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, computed, signal } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { ScheduleService } from '../../services/schedule.service';
import { JobToSchedule, ScheduleBlock, SchedulerFitter, formatMinutes, toIsoDay } from '../../models/schedule.models';
import {
  BoardAbsence, BoardBlock, Candidate, DayRange, FitterDay, HoursLookup, Verdict, boardRange, insideHours, offHours,
  hhmm, minutesAtX, placementVerdict, snapMin, xOf,
} from '../../board/board-geometry';
import { HistoryOp, ScheduleHistory, SlotSpec } from '../../board/schedule-history';

interface ViewOption { key: string; label: string; days: number; pxPerHour: number; weekAligned: boolean; }

/** Same scales as the CGL timeline's day / 2-day / week / fortnight views. */
const VIEWS: ViewOption[] = [
  { key: 'day', label: 'Day', days: 1, pxPerHour: 96, weekAligned: false },
  { key: '2days', label: '2 Days', days: 2, pxPerHour: 62, weekAligned: false },
  { key: 'week', label: 'Week', days: 7, pxPerHour: 26, weekAligned: true },
  { key: '2weeks', label: '2 Weeks', days: 14, pxPerHour: 15, weekAligned: true },
];

const LABEL_PX = 210;
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 8;
const ZOOM_STEP = 1.25;
const CLICK_SLOP_PX = 4;
const EDGE_PX = 56;
const MAX_SCROLL_PX_PER_S = 1400;

type DragMode = 'new' | 'move' | 'resize-start' | 'resize-end';

interface DragState {
  mode: DragMode;
  blockId: number | null;
  job: JobToSchedule | null;
  fitterId: number | null;
  date: string | null;
  durMin: number;
  grabOffsetMin: number;
  label: string;
  originX: number;
  originY: number;
}

interface Hover extends Candidate { verdict: Verdict; }

/**
 * Workshop scheduler board, modelled on the CGL timeline (custom pointer-event drag, no library):
 * a row per fitter, the 07:00-18:00 working day per column, defect jobs and inspections as blocks.
 * Drag from "To schedule", move between fitters/times, resize from either edge (15-minute snap) with a
 * live green/red preview; click a block for its menu. Saves are optimistic and put the board back if
 * refused. Ctrl+Z / Ctrl+Y undo and redo drops, moves, resizes and removals.
 */
@Component({
    selector: 'app-scheduler',
    templateUrl: './scheduler.component.html',
    styleUrls: ['./scheduler.component.scss'],
    standalone: false
})
export class SchedulerComponent implements OnInit, OnDestroy {
  @ViewChild('board', { static: true }) boardEl: ElementRef<HTMLDivElement>;

  readonly views = VIEWS;
  readonly labelPx = LABEL_PX;
  readonly hhmm = hhmm;
  readonly formatMinutes = formatMinutes;

  readonly viewKey = signal<string>('day');
  readonly anchor = signal<string>(toIsoDay(new Date()));
  readonly zoom = signal<number>(1);
  readonly fitters = signal<SchedulerFitter[]>([]);
  readonly absences = signal<BoardAbsence[]>([]);
  readonly blocks = signal<BoardBlock[]>([]);
  /** Working hours: hours()[fitterId][date] (docs/superpowers/specs/2026-10-21-scheduler-hours-design.md). */
  readonly hours = signal<Record<number, Record<string, FitterDay>>>({});
  readonly jobs = signal<JobToSchedule[]>([]);
  readonly drag = signal<DragState | null>(null);
  readonly hover = signal<Hover | null>(null);
  readonly pointer = signal<{ x: number, y: number }>({ x: 0, y: 0 });
  readonly nowMs = signal<number>(Date.now());

  readonly showJobs = signal<boolean>(true);

  search: string = '';
  error: string = '';
  toast: string = '';
  loading: boolean = false;
  busy: boolean = false;

  /** Block whose menu is open, and where. */
  menu: { block: BoardBlock, x: number, y: number } | null = null;
  dialog: 'complete' | 'remaining' | 'notes' | null = null;
  actualHours: number = 1;
  remainingHours: number = 1;
  remainingMinutes: number = 0;
  notes: string = '';

  readonly history = new ScheduleHistory();

  readonly view = computed(() => VIEWS.find((v) => v.key === this.viewKey()) || VIEWS[0]);
  readonly pxPerHour = computed(() => this.view().pxPerHour * this.zoom());
  /** A fitter's hours on a date, or null while not loaded. */
  readonly hoursFor: HoursLookup = (fitterId, date) => {
    const f = this.hours()[fitterId];
    return (f && f[date]) || null;
  };

  /** The part of each day shown: from the earliest start to the latest end of everyone in view. */
  readonly range = computed<DayRange>(() => boardRange(
    this.fitters().flatMap((f) => this.days().map((d) => this.hoursFor(f.id, d)).filter((h): h is FitterDay => !!h)),
    this.blocks().filter((b) => this.days().includes(b.date))));
  readonly dayWidth = computed(() => ((this.range().endMin - this.range().startMin) / 60) * this.pxPerHour());

  /** Visible days, Monday-aligned for the week views. */
  readonly days = computed(() => {
    const view = this.view();
    let start = parseDay(this.anchor());
    if (view.weekAligned) {
      start = addDays(start, -((start.getDay() + 6) % 7));
    }
    return Array.from({ length: view.days }, (_, i) => toIsoDay(addDays(start, i)));
  });
  readonly trackWidth = computed(() => this.dayWidth() * this.days().length);

  /** Hour ticks for one day column: every hour when there is room, else every 2 or 4 hours. */
  readonly ticks = computed(() => {
    const px = this.pxPerHour();
    const step = px >= 60 ? 1 : px >= 24 ? 2 : 4;
    const out: { hour: number, x: number }[] = [];
    const range = this.range();
    for (let h = range.startMin / 60; h < range.endMin / 60; h += step) {
      const x = (h - range.startMin / 60) * px;
      // leave room for the label so it does not run into the next day's 07:00
      if (x + 32 <= this.dayWidth()) {
        out.push({ hour: h, x });
      }
    }
    return out;
  });

  readonly nowX = computed(() => {
    const now = new Date(this.nowMs());
    const today = toIsoDay(now);
    const min = now.getHours() * 60 + now.getMinutes();
    if (!this.days().includes(today) || min < this.range().startMin || min > this.range().endMin) {
      return null;
    }
    return xOf(this.days(), today, min, this.pxPerHour(), this.range());
  });

  readonly nowLabel = computed(() => {
    const now = new Date(this.nowMs());
    return hhmm(now.getHours() * 60 + now.getMinutes());
  });

  private timer: any;
  private searchTimer: any;
  private toastTimer: any;
  /** Only the newest load() may update the board; older responses (and ones overtaken by a local change) are dropped. */
  private loadSeq = 0;
  /** An undo/redo is on its way to the server; the next one waits for it. */
  private replaying = false;
  private scrollFrame: number | null = null;
  private lastFrame = 0;

  constructor(private service: ScheduleService) { }

  ngOnInit(): void {
    this.load(true);
    this.loadJobs();
    this.timer = setInterval(() => this.nowMs.set(Date.now()), 30000);
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    clearTimeout(this.searchTimer);
    clearTimeout(this.toastTimer);
    this.stopAutoScroll();
  }

  // --- loading -----------------------------------------------------------------------------------

  /** Loads the visible range; `scrollToNow` brings today's working time into view afterwards. */
  load(scrollToNow = false): void {
    const days = this.days();
    const from = days[0];
    const to = days[days.length - 1];
    const seq = ++this.loadSeq;
    this.loading = true;
    forkJoin([this.service.fitters(from, to), this.service.blocks(from, to)]).pipe(take(1)).subscribe({
      next: ([f, b]) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.error = '';
        this.fitters.set(f.fitters);
        this.absences.set(f.absences);
        this.hours.set(f.hours || {});
        this.blocks.set(b.map(toBoardBlock));
        this.loading = false;
        if (scrollToNow) {
          setTimeout(() => this.scrollToNow());
        }
      },
      error: (e) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.loading = false;
        this.error = message(e, 'The schedule could not be loaded.');
      },
    });
  }

  private scrollToNow(): void {
    const today = toIsoDay(new Date());
    const el = this.boardEl.nativeElement;
    if (this.days().includes(today)) {
      const x = this.nowX() ?? xOf(this.days(), today, this.range().startMin, this.pxPerHour(), this.range());
      el.scrollLeft = Math.max(0, x - (el.clientWidth - LABEL_PX) / 3);
    } else {
      el.scrollLeft = 0;
    }
  }

  loadJobs(): void {
    this.service.toSchedule(this.search).pipe(take(1)).subscribe({
      next: (jobs) => this.jobs.set(jobs),
      error: () => this.jobs.set([]),
    });
  }

  onSearch(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadJobs(), 250);
  }

  // --- navigation & zoom -------------------------------------------------------------------------

  setView(key: string): void {
    this.viewKey.set(key);
    this.zoom.set(1);
    this.load(true);
  }

  step(direction: -1 | 0 | 1): void {
    if (direction === 0) {
      this.anchor.set(toIsoDay(new Date()));
    } else {
      this.anchor.set(toIsoDay(addDays(parseDay(this.anchor()), direction * this.view().days)));
    }
    this.load(true);
  }

  onDateInput(value: string): void {
    if (value) {
      this.anchor.set(value);
      this.load(true);
    }
  }

  title(): string {
    const days = this.days();
    const fmt = (d: string, o: Intl.DateTimeFormatOptions) => parseDay(d).toLocaleDateString('en-GB', o);
    return days.length === 1
      ? fmt(days[0], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      : `${fmt(days[0], { day: 'numeric', month: 'short' })} – ${fmt(days[days.length - 1], { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }

  dayHeading(date: string): string {
    const opts: Intl.DateTimeFormatOptions = this.dayWidth() >= 120
      ? { weekday: 'short', day: 'numeric', month: 'short' } : { weekday: 'narrow', day: 'numeric' };
    return parseDay(date).toLocaleDateString('en-GB', opts);
  }

  /** Ctrl/Cmd + wheel zooms (0.4x-8x); a plain wheel scrolls. */
  onWheel(event: WheelEvent): void {
    if (!(event.ctrlKey || event.metaKey)) {
      return;
    }
    event.preventDefault();
    this.setZoom(this.zoom() * (event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP), event.clientX);
  }

  zoomIn(): void {
    this.setZoom(this.zoom() * ZOOM_STEP);
  }

  zoomOut(): void {
    this.setZoom(this.zoom() / ZOOM_STEP);
  }

  zoomReset(): void {
    this.setZoom(1);
  }

  canZoomIn(): boolean {
    return this.zoom() < ZOOM_MAX;
  }

  canZoomOut(): boolean {
    return this.zoom() > ZOOM_MIN;
  }

  /** Changes the zoom keeping the time under `clientX` (default: the middle of the board) where it was. */
  private setZoom(next: number, clientX?: number): void {
    const el = this.boardEl.nativeElement;
    const before = this.zoom();
    const after = Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next)) * 1000) / 1000;
    if (after === before) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const offset = clientX === undefined ? (el.clientWidth + LABEL_PX) / 2 : clientX - rect.left;
    const trackX = el.scrollLeft + Math.max(LABEL_PX, offset) - LABEL_PX;
    this.zoom.set(after);
    setTimeout(() => el.scrollLeft = Math.max(0, trackX * (after / before) - (Math.max(LABEL_PX, offset) - LABEL_PX)));
  }

  // --- geometry used by the template -------------------------------------------------------------

  left(b: { date: string, startMin: number }): number {
    return xOf(this.days(), b.date, b.startMin, this.pxPerHour(), this.range());
  }

  width(durMin: number): number {
    return Math.max(2, (durMin / 60) * this.pxPerHour());
  }

  blocksFor(fitterId: number): BoardBlock[] {
    const days = this.days();
    return this.blocks().filter((b) => b.fitterId === fitterId && days.includes(b.date));
  }

  /** Absence days of a fitter that fall in view, as hatched columns. */
  absenceDays(fitterId: number): { date: string, label: string }[] {
    return this.days().flatMap((d) => {
      const a = this.absences().find((x) => x.fitterId === fitterId && x.startDate <= d && x.endDate >= d);
      return a ? [{ date: d, label: a.typeName || 'Absent' }] : [];
    });
  }

  /** Planned and actual hours, and load against the working days shown. */
  totals(fitterId: number): { planned: number, actual: number, load: number | null } {
    const mine = this.blocksFor(fitterId);
    const planned = mine.reduce((s, b) => s + b.durMin, 0);
    const actual = mine.filter((b) => b.status === 'done').reduce((s, b) => s + Number(b.actualHours || 0), 0);
    const working = this.days().reduce((sum, d) => {
      const h = this.hoursFor(fitterId, d);
      return sum + (h ? h.windows.reduce((t, w) => t + w.endMin - w.startMin, 0) : 0);
    }, 0);
    return { planned, actual: Math.round(actual * 100) / 100, load: working ? Math.round(planned / working * 100) : null };
  }

  isOver(b: BoardBlock): boolean {
    return b.status === 'done' && Number(b.actualHours) * 60 > b.durMin;
  }

  blockLabel(b: BoardBlock): string {
    return `${b.registration} · ${b.title}`;
  }

  blockTip(b: BoardBlock): string {
    let tip = `${b.registration} – ${b.title}\n${hhmm(b.startMin)}–${hhmm(b.startMin + b.durMin)} (${formatMinutes(b.durMin)} planned)`;
    if (this.isOutside(b)) {
      tip += '\nOutside this fitter\'s working hours – move it into their hours';
    }
    if (b.status === 'done') {
      tip += `\nDone: ${b.actualHours}h actual`;
      if (this.isOver(b)) {
        tip += ` – over the ${formatMinutes(b.durMin)} planned`;
      }
    }
    return tip;
  }

  /** Work booked for a later day cannot be signed off yet. */
  isFuture(b: BoardBlock): boolean {
    return b.date > toIsoDay(new Date());
  }

  /** Grey (not working) stretches of a fitter's row; a closed day carries its reason. Absence days are drawn separately. */
  offHoursFor(fitterId: number): { date: string, startMin: number, endMin: number, closed: boolean, reason: string }[] {
    const range = this.range();
    return this.days().flatMap((date) => {
      const h = this.hoursFor(fitterId, date) || { windows: [], closed: true, closedReason: null };
      const closed = h.closed || !h.windows.length;
      return offHours(h, range).map((g) => ({ date, ...g, closed, reason: closed ? (h.closedReason || 'Not working') : '' }));
    });
  }

  /** A planned block that no longer sits in its fitter's hours (they were changed after it was booked). */
  isOutside(b: BoardBlock): boolean {
    return b.status === 'planned' && b.id > 0 && !insideHours(this.hoursFor(b.fitterId, b.date), b.startMin, b.startMin + b.durMin);
  }

  isDueOverdue(job: JobToSchedule): boolean {
    return !!job.dueDate && job.dueDate < toIsoDay(new Date());
  }

  // --- drag: start ---------------------------------------------------------------------------------

  startJobDrag(event: PointerEvent, job: JobToSchedule): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    this.closeMenu();
    this.beginDrag(event, {
      mode: 'new', blockId: null, job, fitterId: null, date: null, durMin: job.minutes, grabOffsetMin: 0,
      label: `${job.registration} · ${job.title}`,
    });
  }

  startBlockDrag(event: PointerEvent, b: BoardBlock, mode: 'move' | 'resize-start' | 'resize-end'): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (b.id < 0) {
      return; // still being saved
    }
    this.closeMenu();
    const grab = mode === 'move' ? this.minuteUnder(event.clientX, b.fitterId, b.date) - b.startMin : 0;
    this.beginDrag(event, {
      mode, blockId: b.id, job: null, fitterId: b.fitterId, date: b.date, durMin: b.durMin,
      grabOffsetMin: Number.isFinite(grab) ? grab : 0, label: this.blockLabel(b),
    });
  }

  private beginDrag(event: PointerEvent, d: Omit<DragState, 'originX' | 'originY'>): void {
    this.drag.set({ ...d, originX: event.clientX, originY: event.clientY });
    this.pointer.set({ x: event.clientX, y: event.clientY });
    this.hover.set(null);
  }

  // --- drag: move / drop (document level, so a drag can start in the side panel) -----------------

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    const d = this.drag();
    if (!d) {
      return;
    }
    this.pointer.set({ x: event.clientX, y: event.clientY });
    this.updateHover();
    this.autoScroll();
  }

  @HostListener('document:pointerup', ['$event'])
  onPointerUp(event: PointerEvent): void {
    const d = this.drag();
    if (!d) {
      return;
    }
    this.stopAutoScroll();
    const moved = Math.hypot(event.clientX - d.originX, event.clientY - d.originY);
    const hover = this.hover();
    this.drag.set(null);
    this.hover.set(null);

    if (d.mode !== 'new' && moved <= CLICK_SLOP_PX) {
      const block = this.blocks().find((b) => b.id === d.blockId);
      if (block) {
        this.openMenu(block, event.clientX, event.clientY);
      }
      return;
    }
    if (!hover) {
      return;
    }
    if (!hover.verdict.ok) {
      this.showToast(hover.verdict.message);
      return;
    }
    if (d.mode === 'new') {
      this.dropNew(d.job, hover);
    } else {
      this.applyMove(d.blockId, hover);
    }
  }

  /** The browser took the pointer away (touch scroll, pen lifted out of range...): drop the drag. */
  @HostListener('document:pointercancel')
  cancelDrag(): void {
    this.drag.set(null);
    this.hover.set(null);
    this.stopAutoScroll();
  }

  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.cancelDrag();
      this.closeMenu();
      return;
    }
    const target = event.target as HTMLElement;
    if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) {
      return;
    }
    const mod = event.ctrlKey || event.metaKey;
    if (mod && event.key.toLowerCase() === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.undo();
    } else if (mod && (event.key.toLowerCase() === 'y' || (event.key.toLowerCase() === 'z' && event.shiftKey))) {
      event.preventDefault();
      this.redo();
    }
  }

  /** Works out where the dragged block would land and whether it can go there. */
  private updateHover(): void {
    const d = this.drag();
    const p = this.pointer();
    if (!d) {
      return;
    }
    if (d.mode === 'resize-start' || d.mode === 'resize-end') {
      const b = this.blocks().find((x) => x.id === d.blockId);
      if (!b) {
        this.cancelDrag(); // the board reloaded under the drag
        return;
      }
      const raw = this.minuteUnder(p.x, b.fitterId, b.date);
      if (!Number.isFinite(raw)) {
        return;
      }
      const end = b.startMin + b.durMin;
      const candidate = d.mode === 'resize-end'
        ? { fitterId: b.fitterId, date: b.date, startMin: b.startMin, durMin: Math.max(15, snapMin(raw) - b.startMin) }
        : { fitterId: b.fitterId, date: b.date, startMin: Math.min(snapMin(raw), end - 15), durMin: end - Math.min(snapMin(raw), end - 15) };
      this.hover.set({ ...candidate, verdict: placementVerdict(this.blocks(), this.absences(), this.hoursFor, candidate, b.id) });
      return;
    }

    const lane = (document.elementFromPoint(p.x, p.y) as HTMLElement | null)?.closest('[data-fitter]') as HTMLElement | null;
    if (!lane) {
      this.hover.set(null);
      return;
    }
    const fitterId = Number(lane.dataset['fitter']);
    const at = minutesAtX(this.days(), p.x - lane.getBoundingClientRect().left, this.pxPerHour(), this.range());
    if (!at) {
      this.hover.set(null);
      return;
    }
    const candidate = { fitterId, date: at.date, startMin: snapMin(at.min - d.grabOffsetMin), durMin: d.durMin };
    this.hover.set({ ...candidate, verdict: placementVerdict(this.blocks(), this.absences(), this.hoursFor, candidate, d.blockId) });
  }

  /** Minutes under a pointer x on a given fitter's track, pinned to a day (for resizing). */
  private minuteUnder(clientX: number, fitterId: number, date: string): number {
    const lane = this.boardEl.nativeElement.querySelector(`[data-fitter="${fitterId}"]`) as HTMLElement | null;
    if (!lane) {
      return NaN;
    }
    const dayIndex = this.days().indexOf(date);
    const x = clientX - lane.getBoundingClientRect().left - dayIndex * this.dayWidth();
    return this.range().startMin + (x / this.pxPerHour()) * 60;
  }

  // --- auto-scroll near the board edges --------------------------------------------------------------

  private autoScroll(): void {
    if (this.scrollFrame === null) {
      this.lastFrame = performance.now();
      this.scrollFrame = requestAnimationFrame((t) => this.scrollStep(t));
    }
  }

  private scrollStep(time: number): void {
    const el = this.boardEl.nativeElement;
    const rect = el.getBoundingClientRect();
    const p = this.pointer();
    const seconds = Math.min(0.05, (time - this.lastFrame) / 1000);
    this.lastFrame = time;
    const speed = (depth: number) => MAX_SCROLL_PX_PER_S * Math.min(1, depth / EDGE_PX) * seconds;
    let dx = 0;
    let dy = 0;
    if (p.x < rect.left + LABEL_PX + EDGE_PX && p.x > rect.left + LABEL_PX) dx = -speed(rect.left + LABEL_PX + EDGE_PX - p.x);
    else if (p.x > rect.right - EDGE_PX) dx = speed(p.x - (rect.right - EDGE_PX));
    if (p.y < rect.top + EDGE_PX) dy = -speed(rect.top + EDGE_PX - p.y);
    else if (p.y > rect.bottom - EDGE_PX) dy = speed(p.y - (rect.bottom - EDGE_PX));

    if ((dx || dy) && this.drag()) {
      const before = [el.scrollLeft, el.scrollTop];
      el.scrollLeft += dx;
      el.scrollTop += dy;
      if (el.scrollLeft !== before[0] || el.scrollTop !== before[1]) {
        this.updateHover();
      }
      this.scrollFrame = requestAnimationFrame((t) => this.scrollStep(t));
    } else {
      this.scrollFrame = null;
    }
  }

  private stopAutoScroll(): void {
    if (this.scrollFrame !== null) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  // --- saving (optimistic, put back on failure) -------------------------------------------------------

  private dropNew(job: JobToSchedule, at: Candidate): void {
    const spec: SlotSpec = { fitterId: at.fitterId, jobType: job.jobType, linkId: job.id, startAt: wall(at.date, at.startMin), endAt: wall(at.date, at.startMin + at.durMin) };
    const temp: BoardBlock = {
      id: -Date.now(), fitterId: at.fitterId, date: at.date, startMin: at.startMin, durMin: at.durMin, jobType: job.jobType,
      status: 'planned', registration: job.registration, title: job.title, colour: job.colour, actualHours: null, notes: null,
      workshopDefectId: job.jobType === 'defect' ? job.id : null, vehicleInspectionId: job.jobType === 'inspection' ? job.id : null,
    };
    this.setLocal([...this.blocks(), temp]);
    // Off the list straight away, so it cannot be dropped twice while this save is on its way.
    this.jobs.set(this.jobs().filter((j) => !(j.jobType === job.jobType && j.id === job.id)));
    this.save(this.createFrom(spec), () => {
      this.setLocal(this.blocks().filter((x) => x.id !== temp.id));
      this.loadJobs();
    }, (saved) => {
      this.history.record({ kind: 'create', id: saved.id, spec });
      this.loadJobs();
    });
  }

  private applyMove(blockId: number, at: Candidate): void {
    const b = this.blocks().find((x) => x.id === blockId);
    if (!b || (b.fitterId === at.fitterId && b.date === at.date && b.startMin === at.startMin && b.durMin === at.durMin)) {
      return;
    }
    const beforeSpec = specOf(b);
    const afterSpec: SlotSpec = { ...beforeSpec, fitterId: at.fitterId, startAt: wall(at.date, at.startMin), endAt: wall(at.date, at.startMin + at.durMin) };
    this.setLocal(this.blocks().map((x) => x.id === blockId ? { ...x, fitterId: at.fitterId, date: at.date, startMin: at.startMin, durMin: at.durMin } : x));
    this.save(this.service.move(blockId, afterSpec.fitterId, afterSpec.startAt, afterSpec.endAt), () => {
      this.setLocal(this.blocks().map((x) => x.id === blockId ? { ...x, fitterId: b.fitterId, date: b.date, startMin: b.startMin, durMin: b.durMin } : x));
    }, () => {
      this.history.record({ kind: 'update', id: blockId, before: beforeSpec, after: afterSpec });
    });
  }

  private createFrom(spec: SlotSpec): Observable<ScheduleBlock> {
    const job = { id: spec.linkId, jobType: spec.jobType } as JobToSchedule;
    return this.service.create(job, spec.fitterId, spec.startAt, spec.endAt);
  }

  /** An optimistic change to the board; any load already on its way is now out of date. */
  private setLocal(blocks: BoardBlock[]): void {
    this.loadSeq++;
    this.blocks.set(blocks);
  }

  /**
   * Runs a save; on success reloads the board from the server. On failure `revert` puts back just the block
   * this save changed (other saves may have landed meanwhile), then the board is reloaded and the reason shown.
   */
  private save(request: Observable<any>, revert: () => void, done: (result: any) => void): void {
    request.pipe(take(1)).subscribe({
      next: (result) => {
        done(result);
        this.load();
      },
      error: (e) => {
        revert();
        this.load();
        this.showToast('That change was not saved – the board has been put back. ' + message(e, ''));
      },
    });
  }

  // --- undo / redo ---------------------------------------------------------------------------------

  canUndo(): boolean {
    return !this.replaying && this.history.canUndo();
  }

  canRedo(): boolean {
    return !this.replaying && this.history.canRedo();
  }

  undo(): void {
    if (this.replaying) {
      return;
    }
    const op = this.history.takeUndo();
    if (op) {
      this.replay(op, true);
    }
  }

  redo(): void {
    if (this.replaying) {
      return;
    }
    const op = this.history.takeRedo();
    if (op) {
      this.replay(op, false);
    }
  }

  private replay(op: HistoryOp, undoing: boolean): void {
    this.replaying = true;
    const finished = () => {
      this.replaying = false;
      this.load();
      this.loadJobs();
    };
    const fail = (e: any) => {
      this.replaying = false;
      this.history.clear();
      this.showToast((undoing ? 'Could not undo: ' : 'Could not redo: ') + message(e, 'the board has changed.'));
      this.load();
    };
    const recreate = (spec: SlotSpec) => this.createFrom(spec).pipe(take(1)).subscribe({
      next: (saved) => { this.history.remapId(op.id, saved.id); finished(); },
      error: fail,
    });
    const removeIt = () => this.service.remove(op.id, true).pipe(take(1)).subscribe({ next: finished, error: fail });
    const place = (spec: SlotSpec) => this.service.move(op.id, spec.fitterId, spec.startAt, spec.endAt).pipe(take(1))
      .subscribe({ next: finished, error: fail });

    if (op.kind === 'create') {
      undoing ? removeIt() : recreate(op.spec);
    } else if (op.kind === 'remove') {
      undoing ? recreate(op.spec) : removeIt();
    } else {
      place(undoing ? op.before : op.after);
    }
  }

  // --- block menu & dialogs ----------------------------------------------------------------------------

  openMenu(block: BoardBlock, x: number, y: number): void {
    this.menu = { block, x: Math.min(x, window.innerWidth - 230), y: Math.min(y, window.innerHeight - 240) };
    this.dialog = null;
  }

  closeMenu(): void {
    this.menu = null;
    this.dialog = null;
  }

  openDialog(kind: 'complete' | 'remaining' | 'notes'): void {
    const b = this.menu.block;
    this.dialog = kind;
    this.notes = b.notes || '';
    this.actualHours = b.durMin / 60;
    this.remainingHours = Math.floor(b.durMin / 60);
    this.remainingMinutes = b.durMin % 60;
  }

  complete(): void {
    const b = this.menu.block;
    if (!(this.actualHours >= 0 && this.actualHours <= 24)) {
      this.showToast('Enter the actual hours (0 to 24).');
      return;
    }
    this.busy = true;
    this.service.complete(b.id, Number(this.actualHours), this.notes).pipe(take(1)).subscribe({
      next: () => { this.busy = false; this.history.forget(b.id); this.closeMenu(); this.load(); this.loadJobs(); },
      error: (e) => { this.busy = false; this.showToast(message(e, 'The job could not be completed.')); },
    });
  }

  /** "Hours left": the block becomes that long from its start - shorter, or longer if running over. */
  saveRemaining(): void {
    const b = this.menu.block;
    const durMin = Math.max(15, snapMin(Number(this.remainingHours) * 60 + Number(this.remainingMinutes)));
    const candidate = { fitterId: b.fitterId, date: b.date, startMin: b.startMin, durMin };
    const verdict = placementVerdict(this.blocks(), this.absences(), this.hoursFor, candidate, b.id);
    if (!verdict.ok) {
      this.showToast(verdict.message);
      return;
    }
    this.closeMenu();
    this.applyMove(b.id, candidate);
  }

  saveNotes(): void {
    const b = this.menu.block;
    this.service.saveNotes(b.id, this.notes).pipe(take(1)).subscribe({
      next: () => { this.closeMenu(); this.load(); },
      error: (e) => this.showToast(message(e, 'The notes could not be saved.')),
    });
  }

  remove(): void {
    const b = this.menu.block;
    if (!confirm(`Remove ${b.registration} – ${b.title} from the schedule?`)) {
      return;
    }
    const spec = specOf(b);
    const undoable = b.status === 'planned';
    this.closeMenu();
    this.setLocal(this.blocks().filter((x) => x.id !== b.id));
    this.save(this.service.remove(b.id), () => this.setLocal([...this.blocks(), b]), () => {
      if (undoable) {
        this.history.record({ kind: 'remove', id: b.id, spec });
      }
      this.loadJobs();
    });
  }

  showToast(text: string): void {
    this.toast = text;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast = '', 5000);
  }

  ghostVerdict(): Verdict | null {
    const h = this.hover();
    return h ? h.verdict : null;
  }
}

// --- helpers ---------------------------------------------------------------------------------------

function parseDay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Wall-clock text for a minute of a day; 24:00 is written as 00:00 the next day. */
function wall(date: string, min: number): string {
  return min >= 24 * 60 ? `${toIsoDay(addDays(parseDay(date), 1))} 00:00` : `${date} ${hhmm(min)}`;
}

function minutesOf(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function toBoardBlock(b: ScheduleBlock): BoardBlock {
  const start = minutesOf(b.startAt.slice(11));
  // a block running to midnight ends at 00:00 the next day
  const end = b.endAt.slice(0, 10) !== b.startAt.slice(0, 10) ? 24 * 60 : minutesOf(b.endAt.slice(11));
  return {
    id: b.id, fitterId: b.fitterId, date: b.startAt.slice(0, 10), startMin: start, durMin: end - start,
    jobType: b.jobType, status: b.status, registration: b.registration, title: b.title, colour: b.colour,
    actualHours: b.actualHours, notes: b.notes, workshopDefectId: b.workshopDefectId, vehicleInspectionId: b.vehicleInspectionId,
  };
}

function specOf(b: BoardBlock): SlotSpec {
  return {
    fitterId: b.fitterId, jobType: b.jobType, linkId: b.jobType === 'defect' ? b.workshopDefectId : b.vehicleInspectionId,
    startAt: wall(b.date, b.startMin), endAt: wall(b.date, b.startMin + b.durMin),
  };
}

function message(e: any, fallback: string): string {
  return (e && e.error && e.error.message) || fallback;
}
